// src/pages/PointPages/PointDetails.jsx
import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useTable } from "react-table";
import { mokiApi } from "../../services/loginApi";
import { useParams } from "react-router-dom";

const PointDetails = (props) => {
  // 1) useParams 로 읽어온 값
  const params = useParams();
  // 2) props.storeId 가 있으면 그걸, 없으면 params.store_id
  const store_id = props.storeId ?? params.store_id;
  const phone_num = props.phoneNum ?? params.phone_num;

  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const size = 10;
  const observerRef = useRef(null);

  // 데이터 fetch
  const fetchData = useCallback(
    async (currentPage) => {
      if (currentPage > totalPages) {
        setHasMore(false);
        return;
      }
      try {
        const res = await mokiApi.get(
          `/api/point/details?store_id=${store_id}&phone_num=${phone_num}&page=${currentPage}&size=${size}`
        );
        if (res.data && Array.isArray(res.data.data)) {
          const { page: pageInfo, data: newData } = res.data;
          if (newData.length === 0) {
            setHasMore(false);
          } else {
            setData((prev) => [...prev, ...newData]);
            setTotalPages(pageInfo.totalPages);
            setPage(pageInfo.pageNo + 1);
          }
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error(err);
        setHasMore(false);
      }
    },
    [store_id, phone_num, totalPages]
  );

  // 무한 스크롤
  useEffect(() => {
    if (!hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchData(page);
        }
      },
      { threshold: 0.5 }
    );
    if (observerRef.current) obs.observe(observerRef.current);
    return () => {
      if (observerRef.current) obs.unobserve(observerRef.current);
    };
  }, [page, hasMore, fetchData]);

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      {
        Header: "적립내용",
        accessor: "details",
        Cell: ({ value }) => <b>{value}</b>,
      },
      {
        Header: "포인트",
        accessor: "points",
        Cell: ({ row }) => {
          const { details, points } = row.original;
          const formatted = Number(points).toLocaleString();
          let style = {};
          if (details === "적립") style = { color: "blue" };
          else if (details === "사용") style = { color: "red" };
          const prefix = details === "적립" || details === "지급" ? "+" : "";
          return (
            <span style={style}>
              {prefix}
              {formatted}P
            </span>
          );
        },
      },
      { Header: "날짜", accessor: "date" },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  return (
    // 가로 스크롤 허용 + 최소 너비 확보
    <div className="point_details overflow-x-auto">
      <div className="min-w-max">
        <table
          {...getTableProps()}
          className="point_details_table table-auto w-full"
        >
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                {hg.headers.map((col) => (
                  <th
                    {...col.getHeaderProps()}
                    key={col.id}
                    className="px-4 py-2 whitespace-nowrap text-left font-medium"
                  >
                    {col.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      className="px-4 py-2 whitespace-nowrap"
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {hasMore && (
          <div
            ref={observerRef}
            className="h-12 flex items-center justify-center"
          >
            {/* 로딩 스피너나 메시지를 넣을 수 있습니다 */}
          </div>
        )}
      </div>
    </div>
  );
};

export default PointDetails;
